import type { DerivedMacroSeries, MacroPoint, MacroSeries, TruthState } from "./types";

type AlignablePoint = MacroPoint | { date: string; value: number };
type AlignableSeries = { key: string; points: AlignablePoint[] };

export type AlignedSeriesRow = Record<string, string | number> & { date: string };

const MACROMICRO_HOSTS = new Set(["en.macromicro.me", "macromicro.me"]);

export function macroSourceLinks(
  series: Array<Pick<MacroSeries, "label" | "source_name" | "source_url">>
) {
  const seen = new Set<string>();
  return series.flatMap((item) => {
    if (item.source_name !== "MacroMicro") return [];
    try {
      const parsed = new URL(item.source_url);
      if (parsed.protocol !== "https:" || !MACROMICRO_HOSTS.has(parsed.hostname)) return [];
      const url = `${parsed.origin}${parsed.pathname}`;
      if (seen.has(url)) return [];
      seen.add(url);
      return [{ label: item.label, url }];
    } catch {
      return [];
    }
  });
}

export function mergeSeriesByDate(series: AlignableSeries[]): AlignedSeriesRow[] {
  const rows = new Map<string, AlignedSeriesRow>();

  for (const item of series) {
    for (const point of item.points) {
      const date = "display_date" in point ? point.display_date : point.date;
      const row = rows.get(date) ?? { date };
      row[item.key] = point.value;
      row[`${item.key}__observation_date`] = "observation_date" in point ? point.observation_date : point.date;
      row[`${item.key}__display_date`] = date;
      rows.set(date, row);
    }
  }

  return [...rows.values()].sort((left, right) => left.date.localeCompare(right.date));
}

export type StatePresentation = {
  label: "LIVE" | "DELAYED" | "STALE" | "UNAVAILABLE" | "QUARANTINED" | "OFFLINE";
  tone: "positive" | "warning" | "neutral" | "danger";
  description: string;
};

export function presentationForState(state: TruthState, online = true): StatePresentation {
  if (!online) return { label: "OFFLINE", tone: "warning", description: "Showing the last verified snapshot" };

  const presentations: Record<TruthState, StatePresentation> = {
    live: { label: "LIVE", tone: "positive", description: "Validated source observation" },
    delayed: { label: "DELAYED", tone: "warning", description: "Awaiting the scheduled release" },
    stale: { label: "STALE", tone: "warning", description: "The latest observation is outside its freshness window" },
    unavailable: { label: "UNAVAILABLE", tone: "neutral", description: "No validated observation" },
    quarantined: { label: "QUARANTINED", tone: "danger", description: "Source contract under review" }
  };

  return presentations[state];
}

function csvCell(value: string | number) {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function macroSeriesCsv(series: { key: string; points: MacroPoint[] } | Array<{ key: string; points: MacroPoint[] }>) {
  const items = Array.isArray(series) ? series : [series];
  const rows = ["series_key,observation_date,display_date,value,revision"];
  for (const item of items) {
    for (const point of item.points) {
      rows.push(
        [item.key, point.observation_date, point.display_date, point.value, point.revision].map(csvCell).join(",")
      );
    }
  }
  return `${rows.join("\n")}\n`;
}

export function truthState(value?: string | null): TruthState {
  return value === "live" || value === "delayed" || value === "stale" || value === "quarantined"
    ? value
    : "unavailable";
}

export function derivedSpreadToMacroSeries(derived: DerivedMacroSeries): MacroSeries {
  const latest = derived.points.at(-1);
  const verifiedAt = derived.freshness?.fetched_at ?? derived.fetched_at ?? null;
  return {
    key: "new_orders_minus_inventories",
    label: "New Orders minus Inventories",
    source_name: "Derived",
    source_url: "https://horosho.capital/",
    source_series_id: null,
    unit: "index",
    frequency: "monthly",
    display_shift_months: 2,
    status: derived.status,
    reason_code: derived.status === "unavailable" ? "inputs_unavailable" : null,
    last_success_at: verifiedAt,
    last_observation_date: derived.freshness?.last_observation_date ?? latest?.observation_date ?? null,
    fetched_at: verifiedAt,
    freshness: derived.freshness,
    points: derived.points
  };
}
